// src/pages/auth/LoginPage.tsx
import {useState, useEffect} from "react";
import {Link, useNavigate, useLocation} from "react-router-dom";
import {useDispatch} from "react-redux";
import {Eye, EyeOff, Mail, Lock, AlertCircle, CheckCircle} from "lucide-react";
import {useLoginMutation} from "../../store/api/authApi";
import {setCredentials} from "../../store/slices/authSlice";
import {Button, Input, Card} from "../../components/ui";
import {validateEmail} from "../../utils/validators";
import {ROUTES} from "../../utils/constants";
import type {LoginDto} from "../../types/auth";
import {useToast} from "../../hooks/common/useToast";
import logo from "../../../public/logo.jpg";

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const {success, error: showError} = useToast();

  const [login, {isLoading}] = useLoginMutation();

  // Form state
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    general?: string;
  }>({});

  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Get redirect path from location state
  const from = (location.state as any)?.from?.pathname || ROUTES.DASHBOARD;

  // Handle input changes
  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({...prev, [field]: value}));

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({...prev, [field]: undefined}));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const loginData: LoginDto = {
        email: formData.email,
        password: formData.password,
        type: "email",
      };

      const response = await login(loginData).unwrap();

      if (response.success && response.data) {
        // Store credentials in Redux and localStorage
        dispatch(
          setCredentials({
            user: response.data.user,
            token:
              response.data.tokens?.accessToken || response.data.token || "",
          })
        );

        success("Success", "Login successful! Welcome back.");

        // Navigate to intended page or dashboard
        navigate(from, {replace: true});
      }
    } catch (err: any) {
      console.error("Login error:", err);

      const errorMessage =
        err?.data?.message || "Login failed. Please try again.";

      // Handle specific error cases
      if (
        errorMessage.includes("email") ||
        errorMessage.includes("credential")
      ) {
        setErrors({
          general: "Invalid email or password. Please check your credentials.",
        });
      } else if (errorMessage.includes("verification")) {
        setErrors({
          general: "Please verify your email address before logging in.",
        });
      } else {
        setErrors({general: errorMessage});
      }

      showError("Login Failed", errorMessage);
    }
  };

  // Handle forgot password
  const handleForgotPassword = () => {
    navigate("/auth/forgot-password", {
      state: {email: formData.email},
    });
  };

  // Auto-fill email if coming from registration
  useEffect(() => {
    const emailFromState = (location.state as any)?.email;
    if (emailFromState) {
      setFormData((prev) => ({...prev, email: emailFromState}));
    }
  }, [location.state]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-100 dark:from-dark-900 dark:to-dark-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4">
            <img
              src={logo}
              alt="Logo"
              className="h-16 w-16 object-contain rounded-full"
            />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            Admin Login
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Zaw Htet Real Estate
          </p>
        </div>

        {/* Login Form */}
        <Card className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General Error Message */}
            {errors.general && (
              <div className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4 flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-danger-500 flex-shrink-0" />
                <p className="text-sm text-danger-700 dark:text-danger-400">
                  {errors.general}
                </p>
              </div>
            )}

            {/* Success Message (if any) */}
            {location.state?.successMessage && (
              <div className="bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg p-4 flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-success-500 flex-shrink-0" />
                <p className="text-sm text-success-700 dark:text-success-400">
                  {location.state.successMessage}
                </p>
              </div>
            )}

            {/* Email Field */}
            <div>
              <Input
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="admin@propertyseeker.com"
                leftIcon={<Mail className="w-4 h-4" />}
                error={errors.email}
                disabled={isLoading}
                autoComplete="email"
                autoFocus
              />
            </div>

            {/* Password Field */}
            <div>
              <Input
                label="Password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                placeholder="Enter your password"
                leftIcon={<Lock className="w-4 h-4" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                }
                error={errors.password}
                disabled={isLoading}
                autoComplete="current-password"
              />
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  disabled={isLoading}
                />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                  Remember me
                </span>
              </label>

              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
                disabled={isLoading}
              >
                Forgot password?
              </button>
            </div>

            {/* Login Button */}
            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          {/* Demo Credentials */}
          {/* <div className="mt-6 p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
            <p className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-2">
              Demo Credentials:
            </p>
            <div className="space-y-1">
              <p className="text-xs text-gray-500 dark:text-gray-500">
                Email: admin@propertyseeker.com
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                Password: admin123
              </p>
            </div>
          </div> */}
        </Card>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © 2024 Zwe Htet Real Estate. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
